import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdCheatSheetScopeKey, HdGateInfoService } from '../bodygraph/hd-gate-info.service';

/** Railkleur wanneer de host (nog) geen kleur voor de poort heeft. */
export const HD_CIRCUIT_GATE_CARD_FALLBACK_RAIL = '#3d414e';

/**
 * Het poort-"blokje" van de circuit-/thema-panelen (geheugen, liefde,
 * mystiek): poortnummer en de thema-keynote uit de opgegeven cheat-sheet-scope
 * (`gates.<poort>.keynote`), met een indicatorkleur als linker rail. Leest zijn
 * tekst zelf (taalwissel + NL-fallback), zodat het blokje overal identiek oogt.
 * De host kiest de railkleur (circuitkleur, Figma-kleur, ...).
 */
@Component({
  selector: 'hd-circuit-gate-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'hd-circuit-gate-card',
    '[class.hd-circuit-gate-card--compact]': "size() === 'compact'",
    '[attr.data-gate]': 'gate()',
  },
  template: `
    <span class="hd-circuit-gate-card__rail" [style.background]="rail() || fallbackRail"></span>
    <span class="hd-circuit-gate-card__gate">{{ gate() }}</span>
    <div class="hd-circuit-gate-card__body">
      <p class="hd-circuit-gate-card__keynote">{{ keynote() }}</p>
    </div>
  `,
  styles: `
    :host {
      position: relative;
      display: flex;
      align-items: center;
      gap: 12px;
      background: var(--white);
      border: 1px solid #e6e6e6;
      border-radius: 6px;
      padding: 8px 14px 8px 18px;
      color: #3d414e;
      text-align: left;
    }

    .hd-circuit-gate-card__rail {
      position: absolute;
      left: 0;
      top: 5px;
      bottom: 5px;
      width: 5px;
      border-radius: 3px;
    }

    .hd-circuit-gate-card__gate {
      flex: 0 0 auto;
      min-width: 40px;
      text-align: center;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 26px;
      line-height: 1;
    }

    .hd-circuit-gate-card__body {
      flex: 1 1 auto;
      min-width: 0;
    }

    .hd-circuit-gate-card__keynote {
      margin: 0;
      font-weight: 800;
      font-size: 15.5px;
      line-height: 1.2;
    }

    /* Compacte variant voor smalle panelen: zelfde opbouw, kleinere maten. */
    :host(.hd-circuit-gate-card--compact) {
      gap: 8px;
      padding: 6px 10px 6px 14px;
    }

    :host(.hd-circuit-gate-card--compact) .hd-circuit-gate-card__gate {
      min-width: 30px;
      font-size: 20px;
    }

    :host(.hd-circuit-gate-card--compact) .hd-circuit-gate-card__keynote {
      font-size: 13.5px;
    }
  `,
})
export class HdCircuitGateCardComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Cheat-sheet-scope met `gates.<poort>.keynote` (bv. `memory`, `love`, `mystical`). */
  readonly scope = input.required<HdCheatSheetScopeKey>();
  readonly gate = input.required<number>();
  /** Indicatorkleur van de linker rail. */
  readonly rail = input<string | undefined>(undefined);
  /** `compact` maakt het blokje smaller-vriendelijk voor gedockte panelen. */
  readonly size = input<'default' | 'compact'>('default');

  protected readonly fallbackRail = HD_CIRCUIT_GATE_CARD_FALLBACK_RAIL;

  protected readonly keynote = computed(() =>
    hdCircuitGateText(this.gateInfo, this.contentLang.effectiveLang(), this.scope(), `gates.${this.gate()}.keynote`),
  );
}

/** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege velden. */
export function hdCircuitGateText(
  gateInfo: HdGateInfoService,
  lang: string,
  scope: HdCheatSheetScopeKey,
  key: string,
): string {
  const value = gateInfo.cheatSheetTextIn(scope, key, lang);
  if (value !== '' || lang === 'nl') {
    return value;
  }
  return gateInfo.cheatSheetTextIn(scope, key, 'nl');
}
